/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { goatRepository } from '@/services/repositories/goat.repository.js';

/**
 * Goat Store
 * Manages goat state and provides actions to interact with goat data
 */
export const useGoatStore = defineStore('goat', () => {
  // State
  const goats = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Computed/Getters
  const goatCount = computed(() => goats.value.length);

  const maleGoats = computed(() => 
    goats.value.filter(goat => goat.gender === 'Male')
  );

  const femaleGoats = computed(() => 
    goats.value.filter(goat => goat.gender === 'Female')
  );

  /**
   * Get goats without a group assigned
   * Useful for assigning goats to new groups
   */
  const unassignedGoats = computed(() => 
    goats.value.filter(goat => !goat.groupId)
  );

  /**
   * Get goats enriched with group names from groupStore
   * This computed property performs a virtual JOIN between goats and groups
   * IMPORTANT: This will be populated by components that import both stores
   * @type {import('vue').ComputedRef<Array>}
   */
  const goatsWithGroupNames = ref([]);

  // Actions
  /**
   * Fetch all goats from the database
   * 
   * Caching Strategy:
   * - Stores complete dataset in memory (goats.value)
   * - Components filter locally for better performance
   * - Manual refresh needed after create/update/delete operations
   * - Reduces database queries for offline-first experience
   * 
   * @returns {Promise<void>}
   */
  async function fetchGoats() {
    loading.value = true;
    error.value = null;
    
    try {
      goats.value = await goatRepository.findAll();
      console.log(`Fetched ${goats.value.length} goats`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch goats:', e);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a single goat by ID
   * @param {number} id - Goat ID
   * @returns {Promise<import('../models/goat.model.js').Goat|null>}
   */
  async function getGoatById(id) {
    loading.value = true;
    error.value = null;
    
    try {
      const goat = await goatRepository.findById(id);
      return goat;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch goat:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Add a new goat
   * @param {Omit<import('../models/goat.model.js').Goat, 'id'|'createdAt'|'updatedAt'>} goat
   * @returns {Promise<number|null>} The created goat's ID or null on error
   */
  async function addGoat(goat) {
    loading.value = true;
    error.value = null;
    
    try {
      const id = await goatRepository.create(goat);
      await fetchGoats(); // Refresh the list
      console.log(`Goat created with ID: ${id}`);
      return id;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to add goat:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing goat
   * @param {number} id - Goat ID
   * @param {Partial<import('../models/goat.model.js').Goat>} goat - Updated goat data
   * @returns {Promise<boolean>} Success status
   */
  async function updateGoat(id, goat) {
    loading.value = true;
    error.value = null;
    
    try {
      await goatRepository.update(id, goat);
      await fetchGoats(); // Refresh the list
      console.log(`Goat ${id} updated`);
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to update goat:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a goat
   * @param {number} id - Goat ID
   * @returns {Promise<boolean>} Success status
   */
  async function deleteGoat(id) {
    loading.value = true;
    error.value = null;
    
    try {
      await goatRepository.delete(id);
      await fetchGoats(); // Refresh the list
      console.log(`Goat ${id} deleted`);
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to delete goat:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch goats by group ID (using foreign key)
   * @param {number} groupId - Group ID
   * @returns {Promise<void>}
   */
  async function fetchGoatsByGroupId(groupId) {
    loading.value = true;
    error.value = null;
    
    try {
      goats.value = await goatRepository.findByGroupId(groupId);
      console.log(`Fetched ${goats.value.length} goats from group ID: ${groupId}`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch goats by group ID:', e);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Clear error message
   */
  function clearError() {
    error.value = null;
  }

  return {
    // State
    goats,
    loading,
    error,
    
    // Getters
    goatCount,
    maleGoats,
    femaleGoats,
    unassignedGoats,
    goatsWithGroupNames,
    
    // Actions
    fetchGoats,
    getGoatById,
    addGoat,
    updateGoat,
    deleteGoat,
    fetchGoatsByGroupId,
    clearError
  };
});
